<!-- ToastContainer.vue -->
<!-- 
    This component presents a collection of toast messages. 

    NOTE: There is probably more work but we've got a good head start:
        Clicking on a toast 'Action' link triggers a BroadcastMessage to be sent
            See: MessageLib.Toast.handleAction       
-->
<script lang="ts" setup>
    import { computed, getCurrentInstance, nextTick, onBeforeMount } from 'vue';
    import { ToastsPlacement } from '../../Enums/UiEnums';
    import { ToastItem } from '../../lib/ToastItem';
    import Toast from './Toast.vue';
    import { timeout } from '../../lib/ChoiceUtils';
    import { BootstrapPaddingValue, bootstrapPaddingClass } from '../../lib/StyleHelpers';
    import { assertInternally } from '../../lib/Environment';
    import _ from 'lodash';

    const props = withDefaults(defineProps<{
        toasts?: Array<ToastItem>,
        placement?: ToastsPlacement,
        defaultHideDelayMilliseconds?: number,
        toastPadding?: BootstrapPaddingValue,
        warnIfMessageLibNotAvailable?: boolean
    }>(), {
        placement: ToastsPlacement.BottomRight,
        defaultHideDelayMilliseconds: 10000,
        toastPadding: 3,
        warnIfMessageLibNotAvailable: true
    });

    const emit = defineEmits<{
        shownToast: [ToastItem],
        closedToast: [ToastItem],
        changed: [{widthPx:number, heightPx: number, visibleToastCount: number}],
        clickedAction: [ToastItem]      // AVOID USING THIS!!
    }>();

    const messageLib = computed<any|undefined>(() => 
    {
        if ("undefined" === typeof(MessageLib))
            return undefined;
        return MessageLib;
    });

    onBeforeMount(() => {
        if (!messageLib.value?.Toast?.sink?.handleActionAck)
        {
            if (props.warnIfMessageLibNotAvailable)
            {
                alert("MessageLib.Toast.sink.handleActionAck is not available");
            }
        }
        else
        {
            messageLib.value.Toast.sink.handleActionAck(toastActionHandleMessageAckSink);
        }
    });

    const containerClass = computed<string>(() => `toast-container ${bootstrapPaddingClass(props.toastPadding)} ${props.placement} position-fixed`);

    const hasClickedActionEvent = computed<boolean>(() => {
        const compProps = getCurrentInstance()?.vnode.props;
        return compProps?.onClickedAction;
    });    

    function fireClickAction(toastItem: ToastItem)
    {
        if (hasClickedActionEvent.value)
        {
            emit('clickedAction', toastItem);
            return;
        }

        if (!messageLib.value?.Toast?.handleAction)
        {
            alert("MessageLib.Toast.handleAction is not available");
            return;
        }

        toastStateRecord[toastItem.id] = {addedTimestamp: new Date() };
        // toastItem.data is cloned because the toastItem is reactive and we don't want to pass that reactivity to the message lib
        messageLib.value.Toast.handleAction({ toastId: toastItem.id, data: JSON.parse(JSON.stringify(toastItem.data)) });

        // Make sure the toast was handled
        timeout(150).then(() => {
            const toastState = toastStateRecord[toastItem.id];
            if (toastState)
            {
                if (!toastState.handled)
                {
                    assertInternally("Toast action not handled");
                }
                delete toastStateRecord[toastItem.id];
                onClosedToast(toastItem);
            }
        });
    }

    function toastActionHandleMessageAckSink(payload: MessageLib.Toast.HandleToastActionAckPayload)
    {
        const toastState = toastStateRecord[payload.toastId];
        if (toastState)
            toastState.handled = true;
    }
    const toastStateRecord: Record<string, { addedTimestamp: Date, handled?: boolean }> = {};

    const visibleToastCount = computed(() => props.toasts?.filter(item => item.show).length ?? 0);

    function onAboutToShowToast(toast: ToastItem)
    {
        if (1 === visibleToastCount.value)
        {
            //emit("changed", { widthPx: 50, heightPx: 50, visibleToastCount: visibleToastCount.value });
            emitChanges(50, 50);
        }
    }

    function onAboutToHideToast(toast: ToastItem)
    {
        toast.show = false;
        if (0 === visibleToastCount.value)
        {
            //emit("changed", { widthPx: 0, heightPx: 0, visibleToastCount: visibleToastCount.value });
            emitChanges(0, 0);
            return;
        }

        nextTick(() => computeToastSizeInfo());
    }

    function onShownToast(toast: ToastItem)
    {
        emit('shownToast', toast);
        computeToastSizeInfo();
    }

    function onClosedToast(toast: ToastItem)
    {
        toast.show = false; 
        emit('closedToast', toast);
        computeToastSizeInfo()
    }

    function computeToastSizeInfo()
    {
        const toastElements = document.body.querySelectorAll(".toast.show");
        //const toolTipElements = [...toastElements].map(ele => ele?.getBoundingClientRect()).filter(ele => ele.width);
        const toolTipElements = [...toastElements].map(ele => ele?.getBoundingClientRect());
        if (0 === toolTipElements.length) {
            //emit("changed", { widthPx: 0, heightPx: 0, visibleToastCount: visibleToastCount.value });
            emitChanges(0, 0);
            return;
        }

        // Get the max width of the toasts
        const contentWidth = Math.max(...toolTipElements.map(rect => rect.width));

        // Get the top and bottom of the toasts
        const top = _.minBy(toolTipElements, i => i.top)?.top ?? 0;
        const bottom = _.maxBy(toolTipElements, i => i.bottom)?.bottom ?? 0;
        const contentHeight = bottom - top;

        emitChanges(contentWidth, contentHeight);
    }

    function emitChanges(widthPx: number, heightPx: number)
    {
        emit("changed", { widthPx, heightPx, visibleToastCount: visibleToastCount.value });
    }

</script>

<style>
    /* Make the margin smaller than the default bootstrap class specifies */
    .toast-container>:not(:last-child) {
        margin-bottom: 0.5rem;
    }
    /* Disable the shadow*/
    .toast {
        /*box-shadow: 0.25rem 0.25rem rgba(0, 0, 0, 0.075);*/
        box-shadow: 0rem 0rem;
    }

</style>

<template>
    <div :class="containerClass">
        <template v-for="toast in props.toasts?.filter(aToast => aToast.show) ?? []" :key="toast.id">
            <Toast 
                :id="toast.id"
                :title="toast.title"
                :displayStyle="toast.displayStyle"
                :hideDelayMilliseconds="toast.hideDelayMilliseconds ?? props.defaultHideDelayMilliseconds ?? 5000"
                :hideActionLink="!toast.showActionLink || !messageLib"
                :actionLinkText="toast.actionLinkText"
                @clickedAction="() => fireClickAction(toast)"
                @aboutToShow="() => onAboutToShowToast(toast)"
                @aboutToHide="() => onAboutToHideToast(toast)"
                @shown="() => onShownToast(toast)"
                @closed="() => onClosedToast(toast)">
                {{ toast.msgHtml }}
            </Toast>
        </template>
    </div>
</template>